import {
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import Swal from "sweetalert2";
import useMediaQuery from "@mui/material/useMediaQuery";
import { generateAndExecuteMutation, graphqlQuery } from "../../../../data/Axios/DynamicService";
import Header from "../../../../components/Header";
import { useEffect, useState } from "react";
import { caseManagementUrl, userManagementUrl } from "../../../../config";
import { allUsersQuery } from "../../../../data/Axios/queries";
import { CheckBox } from "@mui/icons-material";


const userbase_url = userManagementUrl.uri;
const base_url = caseManagementUrl.uri;

const TaskForm = (props) => {
  const [usersData, setUsersData] = useState([]);
  const [loading, setLoading] = useState(true);

  const formatDateForInput = (dateString) => {
    if (!dateString) return "";

    // If it's already in YYYY-MM-DD format, return as is
    if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return dateString;
    }

    // If it's a full ISO string, extract the date part
    if (typeof dateString === 'string' && dateString.includes('T')) {
      return dateString.split('T')[0];
    }

    // If it's a Date object, format it
    if (dateString instanceof Date) {
      return dateString.toISOString().split('T')[0];
    }

    // For any other case, try to create a date and format it
    try {
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        return date.toISOString().split('T')[0];
      }
    } catch (e) {
      console.warn("Could not parse date:", dateString);
    }

    return "";
  };

  const initialValues = {
    caseId: props.data?.caseId || "",
    description: props.data?.description || "",
    dueDate: formatDateForInput(props.data?.dueDate), // Format the date here
    asigneeId: props.data?.asigneeId || "",
  };
  console.log("Props Data:", props.data)
  useEffect(() => {
    async function fetchAllUsers() {
      try {
        const allUsersData = await graphqlQuery(allUsersQuery, userbase_url);
        if (allUsersData) {
          setUsersData(allUsersData);
          console.log("Users loaded:", allUsersData);
        }
      } catch (error) {
        console.error("Error fetching advocates/users:", error);
      } finally {
        setLoading(false);
      }
    }
    fetchAllUsers();
  }, []);
  const userOptions = usersData
    ? usersData.map((data) => ({
      value: data.id,
      label: `${data.firstName} ${data.lastName}`,
    }))
    : [];


  const validationSchema = yup.object().shape({
    description: yup.string().required("Task Description is required"),
    dueDate: yup.date().required("Due date is required"),
    asigneeId: yup.number().required("Assignee is required"),
  });

  const isNonMobile = useMediaQuery("(min-width:600px)");
  const caseIdFromSession = JSON.parse(localStorage.getItem("CaseId"));
  const decodedToken = JSON.parse(localStorage.getItem("decodedToken"));
  const userIdFromSession = parseInt(decodedToken.Id);

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const { description, dueDate, asigneeId } = values;
      const numericAsigneeId = parseInt(asigneeId);
      const dueDateUTC = dueDate ? `${dueDate}T00:00:00.000Z` : null;

      const mutationData = props.isEditing
        ? {
          id: props.data.id, // must not be null
          caseId: caseIdFromSession,
          description,
          dueDate: dueDateUTC,
          asigneeId: numericAsigneeId,
          updatedBy: userIdFromSession,
          isActive: 1,
        }
        : {
          caseId: caseIdFromSession,
          description,
          dueDate: dueDateUTC,
          asigneeId: numericAsigneeId,
          createdBy: userIdFromSession,
          isActive: 1,
          isBillable: values.isBillable ? 1 : 0,
          startTimerNow: values.startTimerNow ? 1 : 0,
        };

      const response = await generateAndExecuteMutation(
        props.isEditing ? "updateCaseTask" : "createCaseTask",
        mutationData,
        "newCaseTask",
        base_url
      );

      if (response && Object.values(response)[0] != null) {
        props.onClose?.();
        props.refetchData?.();

        Swal.fire({
          icon: "success",
          title: "Success!",
          text: `Task ${props.isEditing ? "updated" : "created"} successfully.`,
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Failed!",
          text: "Server did not return expected data. Please check API response.",
        });
      }
    } catch (error) {
      console.error("Task creation/update error:", error);
      Swal.fire({
        icon: "error",
        title: "Error!",
        text:
          "An error occurred while processing your request. Please try again later.",
      });
    }
    setSubmitting(false);
  };

  return (
    <Box m="20px">
      <Header
        title={props.isEditing ? "Edit Task" : "Create Task"}
        subtitle={props.isEditing ? "Edit an existing task" : "Create a new task"}
      />
      <Formik
        onSubmit={handleSubmit}
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          isSubmitting,
          setFieldValue
        }) => (
          <form onSubmit={handleSubmit}>
            <Box
              display="grid"
              gap="30px"
              gridTemplateColumns="repeat(4, minmax(0, 1fr))"
              sx={{
                "& > div": { gridColumn: isNonMobile ? undefined : "span 4" },
              }}
            >
              {/* Description */}
              <TextField
                fullWidth
                variant="filled"
                type="text"
                label="Task Description"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.description}
                name="description"
                error={touched.description && !!errors.description}
                helperText={touched.description && errors.description}
                sx={{ gridColumn: "span 4" }}
              />

              {/* Assigned To */}
              <FormControl
                fullWidth
                variant="filled"
                error={touched.asigneeId && !!errors.asigneeId}
                sx={{ gridColumn: "span 4" }}
              >
                <InputLabel id="asignee-label">Assigned To</InputLabel>
                <Select
                  labelId="asignee-label"
                  value={values.asigneeId}
                  label="Assigned To"
                  name="asigneeId"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  displayEmpty
                >
                  <MenuItem value="" disabled>
                    Select Assignee
                  </MenuItem>
                  {userOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
                {touched.asigneeId && errors.asigneeId && (
                  <FormHelperText>{errors.asigneeId}</FormHelperText>
                )}
              </FormControl>

              {/* Due Date */}
              <TextField
                fullWidth
                id="dueDate"
                name="dueDate"
                label="Due Date"
                type="date"
                value={values.dueDate}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.dueDate && Boolean(errors.dueDate)}
                helperText={touched.dueDate && errors.dueDate}
                InputLabelProps={{ shrink: true }}
                sx={{ gridColumn: "span 4" }}
                variant="filled"
              />

              {!props.isEditing && (
                <>
                  {/* Billable and Timer Options */}
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="isBillable"
                        checked={values.isBillable}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setFieldValue("isBillable", checked);

                          // If unchecked, also stop timer
                          if (!checked) {
                            setFieldValue("startTimerNow", false);
                          }
                        }}
                      />
                    }
                    label="Billable"
                  />
                  <FormControlLabel
                    control={
                      <CheckBox
                        name="startTimerNow"
                        checked={values.startTimerNow}
                        onChange={(e) =>
                          setFieldValue("startTimerNow", e.target.checked)
                        }
                        disabled={!values.isBillable}
                      />
                    }
                    label="Start Timer Now"
                  /></>)}
            </Box>
            <Box display="flex" justifyContent="end" mt="20px">
              <Button
                type="submit"
                color="secondary"
                variant="contained"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Submit"}
              </Button>
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default TaskForm;
